import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LogicFrame from "./LogicFrame";
import { LogicImage } from "./LogicImage";
import {
  fetchAbilities, fetchCourse, fetchProgress, fetchPracticeLog, fetchRecords,
  fetchAbilityEvidence, submitAbilityEvidence, resetProgress,
} from "@/lib/logicApi";
import { useLogicUi } from "./logicUi";
import { useT } from "@/i18n/LanguageContext";

const LEVELS = ["Not Yet Encountered", "Seen", "Understood", "Practised", "Applied", "Reflected", "Demonstrated"];

function computeLevel(a, ctx) {
  const { slugByOrder, recs, logs, judgments, evidence } = ctx;
  const related = recs.filter((r) => (a.lessons || []).some((n) => slugByOrder[n] === r.lessonSlug));
  let level = 0;
  if (related.length) level = 1;
  if (related.some((r) => (r.scenarioResponse || "").trim())) level = 2;
  if (related.some((r) => r.exerciseResponse || r.practiceStatus)) level = Math.max(level, 3);
  const appliedByLesson = related.some((r) => ["Applied", "Reflected"].includes(r.practiceStatus) || r.learningStatus === "Applied");
  const appliedByPractice = logs.some((l) => (a.practices || []).includes(l.practiceId));
  if (level >= 3 && (appliedByLesson || appliedByPractice)) level = 4;
  const reflected = related.some((r) => (r.reflection || "").trim()) || judgments.some((j) => (j.afterReflection || "").trim());
  if (level >= 4 && reflected) level = 5;
  if (evidence.some((e) => e.abilityId === a.id)) level = 6;
  return LEVELS[level];
}

function AbilityCard({ a, level, evidence, onEvidence }) {
  const { ui, L } = useLogicUi();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const mine = evidence.find((e) => e.abilityId === a.id);
  const tone = level === "Demonstrated" ? "border-gold/60 text-gold" : level === "Not Yet Encountered" ? "border-slate-500/40 text-slate-400" : "border-[#8fa3c8]/50 text-[#b9c6de]";
  const submit = async () => {
    setErr("");
    try { await submitAbilityEvidence(a.id, text); setText(""); onEvidence(); }
    catch (e) { setErr(e.message); }
  };
  return (
    <div className="border border-[#c8a95a]/15 bg-[#171c26]" data-testid={`ability-card-${a.id}`}>
      <button onClick={() => setOpen(!open)} aria-expanded={open}
        className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 focus:outline-none focus:ring-2 focus:ring-gold/60">
        <span className="text-[15px] text-bone/90 font-reading">{L(a.name)}</span>
        <span className={`text-[10px] tracking-[0.18em] uppercase whitespace-nowrap px-2 py-1 border ${tone}`} data-testid={`ability-level-${a.id}`}>{ui.levels[level] || level}</span>
      </button>
      {open && (
        <div className="px-5 pb-6 space-y-4" data-testid={`ability-detail-${a.id}`}>
          <p className="text-sm text-fog font-reading leading-[1.85]">{L(a.meaning)}</p>
          <p className="text-[13px] text-fog/70 font-reading italic leading-[1.85]">{L(a.grounding)}</p>
          <div className="grid sm:grid-cols-2 gap-4 text-[13px] font-reading leading-relaxed">
            <div className="border-l-2 border-[#8fa3c8]/40 pl-3"><p className="text-[10px] tracking-[0.2em] uppercase text-[#8fa3c8] mb-1">{ui.beginning}</p><p className="text-fog/85">{L(a.beginner)}</p></div>
            <div className="border-l-2 border-gold/50 pl-3"><p className="text-[10px] tracking-[0.2em] uppercase text-gold/80 mb-1">{ui.advanced}</p><p className="text-fog/85">{L(a.advanced)}</p></div>
          </div>
          <p className="text-[13px] text-bone/85 font-reading leading-relaxed"><span className="text-gold/70 text-[10px] tracking-[0.2em] uppercase">{ui.mastery} </span>{L(a.mastery)}</p>
          <p className="text-[13px] text-fog/80 font-reading leading-relaxed"><span className="text-gold/70 text-[10px] tracking-[0.2em] uppercase">{ui.exerciseLbl} </span>{L(a.exercise)}</p>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px] tracking-[0.14em] uppercase text-fog/50 pt-2 border-t border-white/5">
            {a.lessons?.length > 0 && <span>{ui.lessons} {a.lessons.join(", ")}</span>}
            {a.terms?.length > 0 && <span className="text-[#8fa3c8]">{a.terms.join(" · ")}</span>}
          </div>
          <div className="pt-3 border-t border-white/10">
            {mine ? (
              <p className="text-[13px] text-fog/80 font-reading"><span className="text-gold/70">{ui.yourDemo} ({String(mine.at).slice(0, 10)}) · </span>{mine.evidence}</p>
            ) : (
              <>
                <p className="text-xs text-fog/60 font-reading mb-2">
                  {ui.demoHint}
                </p>
                <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} data-testid={`ability-evidence-input-${a.id}`}
                  className="w-full bg-[#f3e9d2]/[0.05] border border-[#c8a95a]/25 text-bone font-reading p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
                {err && <p className="text-xs text-red-300 mt-1">{err}</p>}
                <button data-testid={`ability-evidence-submit-${a.id}`} onClick={submit}
                  className="mt-2 px-5 py-2 border border-gold/40 text-gold text-[10px] tracking-[0.2em] uppercase hover:bg-gold/10">{ui.submitDemo}</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PathOfPractice() {
  const { ui } = useLogicUi();
  const { t } = useT();
  const [abilities, setAbilities] = useState([]);
  const [course, setCourse] = useState(null);
  const [recs, setRecs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [judgments, setJudgments] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [confirm, setConfirm] = useState(false);

  const loadEvidence = () => fetchAbilityEvidence().then((d) => setEvidence(d.evidence || []));
  useEffect(() => {
    fetchAbilities().then((d) => setAbilities(d.abilities || []));
    fetchCourse().then(setCourse).catch(() => {});
    fetchProgress().then((d) => setRecs(d.records || []));
    fetchPracticeLog().then((d) => setLogs(d.logs || []));
    fetchRecords().then((d) => setJudgments(d.records || []));
    loadEvidence();
  }, []);

  if (!course || !abilities.length) return <div className="min-h-screen bg-ink" />;
  const slugByOrder = {};
  course.phases.forEach((p) => p.lessons.forEach((l) => { slugByOrder[l.order] = l.slug; }));
  const ctx = { slugByOrder, recs, logs, judgments, evidence };

  return (
    <LogicFrame title={ui.pathTitle} seoTitle="Path of Practice" crumb={ui.pathCrumb} media={course.media} testid="logic-progress-page">
      <div className="max-w-2xl mb-10 space-y-4">
        <p className="text-fog/90 font-reading leading-[1.9]">
          {ui.pathIntro}
        </p>
      </div>
      <div className="mb-12 max-w-xl"><LogicImage media={course.media} assetKey="logic-08-path-of-practice" alt="" /></div>
      <div className="space-y-3" data-testid="logic-abilities-list">
        {abilities.map((a) => (
          <AbilityCard key={a.id} a={a} level={computeLevel(a, ctx)} evidence={evidence} onEvidence={loadEvidence} />
        ))}
      </div>
      <div className="mt-14 flex flex-wrap gap-6 text-[11px] tracking-[0.18em] uppercase">
        <Link to="/academy/logic/journal" className="text-gold hover:underline">{ui.recordOfJudgment}</Link>
        <Link to="/academy/logic/practice" className="text-[#b9c6de] hover:underline">{ui.practiceBeyond}</Link>
        <Link to="/academy/logic/terms" className="text-[#b9c6de] hover:underline">{ui.theTerms}</Link>
        <Link to="/academy/my-work" className="text-[#b9c6de] hover:underline" data-testid="path-mywork-link">{t("academy.nav.myWork")}</Link>
      </div>
      <div className="mt-12 border-t border-white/10 pt-8">
        {!confirm ? (
          <button data-testid="logic-reset-progress" onClick={() => setConfirm(true)} className="text-[11px] tracking-[0.2em] uppercase text-fog/60 border border-white/15 px-4 py-2 hover:border-red-400/50 hover:text-red-300">{ui.resetBtn}</button>
        ) : (
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-sm text-red-300">{ui.resetWarn}</span>
            <button data-testid="logic-reset-confirm" onClick={async () => { await resetProgress(); setRecs([]); setConfirm(false); }} className="text-[11px] tracking-[0.2em] uppercase text-red-300 border border-red-400/50 px-4 py-2">{ui.resetYes}</button>
            <button onClick={() => setConfirm(false)} className="text-[11px] tracking-[0.2em] uppercase text-fog/70 border border-white/15 px-4 py-2">{ui.resetKeep}</button>
          </div>
        )}
      </div>
    </LogicFrame>
  );
}
